import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button as ChakraButton,
  Flex,
  FormControl,
  FormLabel,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Switch,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiSearch } from 'react-icons/fi';
import * as yup from 'yup';
import {
  Button,
  Heading,
  Input,
  Menu,
  Pagination,
  Select,
  StatusBadge,
  Table,
  Textarea,
} from '~/components';
import {
  useCreateEquipmentModel,
  useEquipmentModels,
  useEquipmentTypesAsSelect,
  useUpdateEquipmentModel,
} from '~/hooks';
import { useDebounce } from '~/hooks/useDebounce';
import { EquipmentModelForm, EquipmentModelView } from '~/types';

const updateFormSchema = yup.object().shape({
  nm_modeloequipamento: yup.string().required('Nome do modelo obrigatório'),
  tp_situacao: yup.boolean(),
  id_tipoequipamento: yup.string().required('Tipo obrigatório'),
});

export const EquipmentModels: React.VFC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isEdition, setIsEdition] = useState(false);
  const { register, handleSubmit, formState, setValue, reset } = useForm({
    resolver: yupResolver(updateFormSchema),
  });
  const { errors } = formState;
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTmp, setSearchTmp] = useState('');
  const updateEquipmentModel = useUpdateEquipmentModel();
  const createEquipmentModel = useCreateEquipmentModel();

  const [currentModel, setCurrentModel] = useState<
    EquipmentModelView | undefined
  >(undefined);

  const {
    data: types,
    isLoading: isTypesLoading,
    isError: isTypesError,
  } = useEquipmentTypesAsSelect();

  const {
    data,
    isLoading: isModelsLoading,
    isError: isModelsError,
    isFetching,
    refetch,
  } = useEquipmentModels(page, search);

  const isLoading = isTypesLoading || isModelsLoading;
  const isError = isTypesError || isModelsError;

  function handleDialogUpdate(model: EquipmentModelView) {
    setIsEdition(true);
    onOpen();
    setCurrentModel(model);
    setValue(
      'id_tipoequipamento',
      String(model?.tipo_equipamento?.id_tipoequipamento),
    );
    setValue('nm_modeloequipamento', model.nm_modeloequipamento);
    setValue('tp_situacao', model.tp_situacao);
    setStatus(model.status);
  }

  function handleDialogCreate() {
    setIsEdition(false);
    reset();
    onOpen();
  }

  async function handleSave(formData: EquipmentModelForm) {
    if (isEdition) {
      await updateEquipmentModel.mutateAsync({
        id: String(currentModel?.id_modeloequipamento),
        formData: {
          ...formData,
          tp_situacao: status ? '1' : '0',
        },
      });
    } else {
      await createEquipmentModel.mutateAsync({
        formData: {
          ...formData,
          tp_situacao: status ? '1' : '0',
        },
      });
    }
    onClose();
    setCurrentModel(undefined);
  }

  useDebounce(
    async () => {
      setPage(1);
      setSearch(searchTmp);
      refetch();
    },
    1000,
    [searchTmp],
  );

  useEffect(() => {
    refetch();
  }, []);

  return (
    <Box px="8" py="4">
      <Heading hasBackButton>Modelos de Equipamento</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={9}>
          <Flex align="center" gridGap={4}>
            <Input
              name="search"
              type="text"
              icon={FiSearch}
              value={searchTmp}
              onChange={e => setSearchTmp(e.target.value)}
              placeholder="Procure por nome ou tipo"
              w={350}
              fontSize="15px"
            />
            {isFetching && <Spinner />}
          </Flex>
          <Button onClick={() => handleDialogCreate()}>Cadastrar</Button>
        </Flex>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : isError ? (
          <Flex justify="center">
            <Text>Falha ao obter dados dos modelos.</Text>
          </Flex>
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Tipo</Th>
                  <Th>Data Atualização</Th>
                  <Th>Status</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {data &&
                  data.results.map(model => (
                    <Tr key={`${model.id_modeloequipamento}`}>
                      <Td>
                        <ChakraButton
                          variant="link"
                          fontWeight="normal"
                          color="inherit"
                          textAlign="left"
                          justifyContent="normal"
                          fontSize="12px"
                          onClick={() => handleDialogUpdate(model)}
                        >
                          {model.nm_modeloequipamento}
                        </ChakraButton>
                      </Td>
                      <Td fontSize="14px">
                        {model?.tipo_equipamento?.nm_tipoequipamento || '-'}
                      </Td>
                      <Td>{model.formattedInitialDate || '-'}</Td>
                      <Td fontSize="14px">
                        <StatusBadge variant={model.colorScheme}>
                          {model.formattedStatus}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuList>
                            <MenuItem onClick={() => handleDialogUpdate(model)}>
                              Editar
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex justify="flex-end">
              {data && (
                <Pagination
                  totalCountOfRegisters={data.count}
                  currentPage={page}
                  onPageChange={setPage}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={undefined}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
        size="lg"
      >
        <AlertDialogOverlay />
        <AlertDialogContent as="form" onSubmit={handleSubmit(handleSave)}>
          <AlertDialogHeader fontSize="md">
            {isEdition
              ? `Atualizar - ${currentModel?.nm_modeloequipamento}`
              : 'Cadastrar Modelo'}
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Stack gridGap={2}>
              <Select
                name="id_tipoequipamento"
                placeholder="Selecione"
                label="Tipo"
                register={register}
                error={errors.id_tipoequipamento}
                isDisabled={isEdition}
              >
                {types &&
                  types.map(type => (
                    <option key={type.value} value={Number(type.value)}>
                      {type.label}
                    </option>
                  ))}
              </Select>

              <Textarea
                resize="none"
                name="nm_modeloequipamento"
                label="Modelo"
                register={register}
                error={errors.nm_modeloequipamento}
                placeholder="Modelo"
                isDisabled={isEdition}
              />
              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <FormLabel htmlFor="status" mb="0">
                  {status ? 'Ativo' : 'Inativo'}
                </FormLabel>
                <Switch
                  id="status"
                  size="lg"
                  colorScheme="green"
                  {...register('tp_situacao')}
                  isChecked={status}
                  onChange={e => setStatus(e.target.checked)}
                />
              </FormControl>
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button onClick={onClose} variant="secondary">
              Cancelar
            </Button>
            <Button ml={3} type="submit">
              Confirmar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};
